// The strategy mix as data (docs/PEDAGOGY.md §2.1, Axel's decisions of 2026-10-06 in §7).
//
// Six measured strategies plus a fixed spine. Every activity in the app carries exactly one
// strategy (ACTIVITIES below); the measurement logs (log.strategy / log.activity in review_log,
// activity_time, session_rating, recording_score) store these ids. Ids are stable and never
// reused, like translator note ids: a renamed strategy keeps its id, a retired one stays listed.
//
// Nothing here drives the app yet (phase 1 only measures): the default shares, floors and caps
// and the block schedule are the data the report (phase 4) and the re-weighting (phase 5) read.
// Pure data and small lookups; no DOM, no Node.
import { isOwnErrorCardId } from './own-errors';

/**
 * The measured strategies (PEDAGOGY §2.1): the six of decision 2, then Lukeminen (Lukupino, Axel's
 * decision of 2026-10-09: reading where 98 % of the words are steady). Reading has no share yet:
 * it is offered on Tänään only when no emphasis offer is due, measured, and not steered (§2.1).
 */
export const STRATEGY_IDS = ['cards', 'forms', 'listening', 'pronunciation', 'writing', 'speaking', 'reading'] as const;
export type StrategyId = (typeof STRATEGY_IDS)[number];

/** The spine: the weekly lesson with its check, and Kielioppi. Not re-weighted, not compared. */
export const SPINE_IDS = ['lesson', 'grammar'] as const;
export type SpineId = (typeof SPINE_IDS)[number];

/** What a log row's `strategy` holds: a measured strategy, or 'spine' for the lesson and Kielioppi. */
export type StrategyTag = StrategyId | 'spine';

export interface Strategy {
  id: StrategyId;
  /** The name in the UI (PEDAGOGY §2.1 table). */
  fi: string;
  en: string;
  /** What it is, in research terms, and the PEDAGOGY sections behind it. */
  research: string;
  /** Solo strategies share the solo study time; speaking is the couple session's fixed block. */
  solo: boolean;
}

export const STRATEGIES: readonly Strategy[] = [
  { id: 'cards', fi: 'Kortit', en: 'Cards', research: 'retrieval + spacing (§1.1, §1.2)', solo: true },
  { id: 'forms', fi: 'Taivutus', en: 'Forms', research: 'interleaved grammar practice (§1.3, §1.4)', solo: true },
  { id: 'listening', fi: 'Kuunteleminen', en: 'Listening', research: 'comprehensible input + perception (§1.5, §1.13)', solo: true },
  {
    id: 'pronunciation',
    fi: 'Ääntäminen',
    en: 'Pronunciation',
    research: 'shadowing + record-and-compare with feedback (§1.12, §1.13)',
    solo: true,
  },
  { id: 'writing', fi: 'Kirjoittaminen', en: 'Writing', research: 'output + peer feedback in writing (§1.6)', solo: true },
  { id: 'speaking', fi: 'Puhuminen yhdessä', en: 'Speaking together', research: 'interaction, peer feedback, tasks, role-play (§1.6–§1.8)', solo: false },
  // Lukupino (2026-10-09; engine pedagogy/reading.ts)
  { id: 'reading', fi: 'Lukeminen', en: 'Reading', research: 'comprehensible input at 98 % coverage, read aloud (§1.5, §1.11)', solo: true },
];

export const isStrategyId = (s: unknown): s is StrategyId => typeof s === 'string' && (STRATEGY_IDS as readonly string[]).includes(s);

export function strategy(id: StrategyId): Strategy {
  return STRATEGIES.find((s) => s.id === id)!;
}

// ---------- activities ----------

/**
 * Every activity the app measures, each with exactly one strategy. The app tags logs with the
 * activity id; the strategy is looked up here, so re-mapping an activity later is a data change.
 * Ids are stable and never reused.
 */
export const ACTIVITIES = {
  // Kortit: the deck (Tänään, Harjoitus, Vapaa harjoitus); form drills inside it are Taivutus
  'cards-daily': 'cards',
  'cards-free': 'cards',
  'form-drill': 'forms',
  // Taivutus: the engine exercises (fill, case, order, translate) outside lesson checks
  'free-exercises': 'forms',
  'shared-items': 'forms',
  // Kuunteleminen: Kuuntele (rounds, scenes, our voices), the week's lines
  listen: 'listening',
  'week-listen': 'listening',
  // "Missä paino?" (phase 3): work out the stressed syllable, then hear Peterson — a perception
  // drill, so Kuunteleminen (PEDAGOGY §2.2), though it lives under Ääntäminen
  'stress-drill': 'listening',
  // listening cards in the deck (card type 'listen', 2026-10-09): hear a word, recall its meaning —
  // Kuunteleminen, as form drills in the deck are Taivutus
  'listen-card': 'listening',
  // Ääntäminen: the Ääntäminen path, record-and-compare, shadowing, form recordings
  pronunciation: 'pronunciation',
  'week-speak': 'pronunciation',
  // Kirjoittaminen: the weekly letter, notebook tasks
  letter: 'writing',
  notebook: 'writing',
  // Puhuminen yhdessä: the couple session's role play and (phase 2) the speaking ladder
  roleplay: 'speaking',
  'speaking-ladder': 'speaking',
  // the spine: the lesson (text and check) and Kielioppi
  lesson: 'spine',
  grammar: 'spine',
  // the alternating weeks' daily offer on Tänään (pedagogy/emphasis.ts, Axel's decision of
  // 2026-10-09): a Kuuntele round in a listening week, shadowing + a Puhu take in a pronunciation week
  'emphasis-listen': 'listening',
  'emphasis-shadow': 'pronunciation',
  // "Kuka teki kenelle?" (pedagogy/role-swap.ts, 2026-10-09): read the roles off the case endings
  // (processing instruction) — grammar practice, so Taivutus
  'role-swap': 'forms',
  // Omat virheet (Axel's decision of 2026-10-09, engine/src/pedagogy/own-errors.ts): a card made from
  // one's own mistake, retrieved again in the daily deck: retrieval + spacing, so Kortit
  'own-errors': 'cards',
  // Lukupino (pedagogy/reading.ts, Axel's decision of 2026-10-09): texts read aloud at 98 % steady words
  reading: 'reading',
  // Ääntämisvalmentaja (pedagogy/coach.ts, Axel's decision of 2026-10-11): the live pronunciation coach
  // under Ääntäminen, for a person who turned it on — production practice with feedback, so Ääntäminen
  coach: 'pronunciation',
  // "Kertaa viikon kielioppi" (pedagogy/grammar-twice.ts, Axel 2026-10-14): the week's Kielioppi topics read again
  // mid-week, then a few rule questions from the lesson's check — part of the week's lesson, so the spine
  'grammar-review': 'spine',
} as const satisfies Record<string, StrategyTag>;

export type ActivityId = keyof typeof ACTIVITIES;

export const isActivityId = (s: unknown): s is ActivityId => typeof s === 'string' && Object.prototype.hasOwnProperty.call(ACTIVITIES, s);

/** The one strategy an activity belongs to. */
export function strategyOf(activity: ActivityId): StrategyTag {
  return ACTIVITIES[activity];
}

/** A card in the deck: form drills are Taivutus, listening cards Kuunteleminen, every other card (word, reverse, sentence) Kortit. */
export function cardActivity(mode: 'daily' | 'free', cardType: string, itemId?: string): ActivityId {
  if (cardType === 'form-drill') return 'form-drill';
  if (cardType === 'listen') return 'listen-card';
  // an error card (Omat virheet: item id "virhe:<id>", own-errors.ts OWN_ERROR_PREFIX)
  if (cardType === 'sentence' && itemId && isOwnErrorCardId(itemId)) return 'own-errors';
  return mode === 'daily' ? 'cards-daily' : 'cards-free';
}

/**
 * Where the app is → the activity being done, for the time measure (`activity_time`) when no
 * screen claimed a finer one (a running deck or exercise round claims its own). First match wins;
 * unmatched paths (Sanakirja, Kääntäjä, Asetukset …) are reference or settings, not study time.
 */
export const ROUTE_ACTIVITIES: readonly { path: RegExp; activity: ActivityId }[] = [
  // "Kuka teki kenelle?" (roles/): Vapaa harjoitus's round and the week's
  { path: /^\/(free|lessons\/[^/]+\/harjoittele)\/kuka\/?$/, activity: 'role-swap' },
  { path: /^\/lessons\/[^/]+\/listen\/?$/, activity: 'week-listen' },
  { path: /^\/lessons\/[^/]+\/speak\/?$/, activity: 'week-speak' },
  { path: /^\/lessons\/[^/]+\/letter\/?$/, activity: 'letter' },
  { path: /^\/lessons\/[^/]+\/together\/?$/, activity: 'roleplay' },
  // the second reading of the week's grammar (pedagogy/grammar-twice.ts)
  { path: /^\/lessons\/[^/]+\/kertaa\/?$/, activity: 'grammar-review' },
  { path: /^\/lessons(\/[^/]+)?\/?$/, activity: 'lesson' },
  { path: /^\/grammar(\/|$)/, activity: 'grammar' },
  // the alternating weeks' offers (pedagogy/emphasis.ts) before the rest of their sections
  { path: /^\/listen\/kierros\/?$/, activity: 'emphasis-listen' },
  { path: /^\/pronunciation\/varjosta\/?$/, activity: 'emphasis-shadow' },
  // Lukupino (pedagogy/reading.ts)
  { path: /^\/reading(\/|$)/, activity: 'reading' },
  { path: /^\/listen(\/|$)/, activity: 'listen' },
  // "Missä paino?" before the rest of Ääntäminen (first match wins)
  { path: /^\/pronunciation\/paino\/?$/, activity: 'stress-drill' },
  // Ääntämisvalmentaja (COACH_BASE, engine/src/coach/contract.ts) before the rest of Ääntäminen
  { path: /^\/pronunciation\/valmentaja(\/|$)/, activity: 'coach' },
  { path: /^\/pronunciation(\/|$)/, activity: 'pronunciation' },
  { path: /^\/letters(\/|$)/, activity: 'letter' },
  { path: /^\/shared\/scene\/?$/, activity: 'roleplay' },
  // the Halloween line, built and recorded together (2026-10-07, docs/PEDAGOGY.md §3.4)
  { path: /^\/shared\/halloween\/?$/, activity: 'roleplay' },
  // Illan tie, the couple evening (2026-10-13): speaking together throughout (Puhutaan is its fourth block)
  { path: /^\/shared\/?$/, activity: 'speaking-ladder' },
];

export function routeActivity(pathname: string): ActivityId | null {
  for (const r of ROUTE_ACTIVITIES) if (r.path.test(pathname)) return r.activity;
  return null;
}

// ---------- sessions (the one tap) ----------

/** The sessions that end with the optional tap (decision 1: both the daily and the couple session). */
export const SESSION_KINDS = ['daily', 'couple'] as const;
export type SessionKind = (typeof SESSION_KINDS)[number];

/** The tap: Hyvin = +1, Ihan ok = 0, Ei oikein = −1 (PEDAGOGY §5.4). */
export const RATINGS = [1, 0, -1] as const;
export type Rating = (typeof RATINGS)[number];

/** What the couple session contains (PEDAGOGY §3.1): the shared items and the speaking part. */
export const COUPLE_SESSION_STRATEGIES: readonly StrategyId[] = ['forms', 'speaking'];

// ---------- shares (decision 2), floors and caps (§6.2) ----------

/**
 * Default shares of solo study time beyond the lesson, in percent (decision 2, 2026-10-06).
 * Lukeminen has none (not steered; see STRATEGY_IDS).
 */
export const DEFAULT_SHARES: Readonly<Record<Exclude<StrategyId, 'speaking' | 'reading'>, number>> = {
  cards: 40,
  forms: 15,
  listening: 20,
  pronunciation: 15,
  writing: 10,
};

export type SoloStrategyId = keyof typeof DEFAULT_SHARES;

/** The couple session's speaking block: fixed, at least this many minutes a week (§2.1, §6.2). */
export const SPEAKING_MIN_MINUTES = 15;

/** §6.2 rule 5: Kortit ≥ 30 %, every other solo strategy ≥ 5 %, none above 50 %. */
export const SHARE_LIMITS = {
  floor: { cards: 30, forms: 5, listening: 5, pronunciation: 5, writing: 5 } as Readonly<Record<SoloStrategyId, number>>,
  cap: 50,
  /** One suggestion moves this many percentage points (§6.2 rule 4). */
  step: 5,
} as const;

// ---------- the block schedule (decision 11) ----------

/**
 * Block ends: the end of the week of these lessons (§6.1). After lesson 9 the report only; from
 * lesson 17 the report and at most one re-weighting suggestion; nothing changes after lesson 41.
 * The dates are the measurement's calendar, eight weeks a block from the course start: the Olla lesson
 * (2026-10-20) moved the lesson numbers up one and kept the dates, as the users' early openings of
 * October 2026 keep every lesson from Perusteet 2 (5) on its old Saturday (docs/COURSE.md "The Olla lesson"),
 * and the blocks were already running (block 1 week 2 on 2026-10-21).
 */
export const BLOCK_ENDS: readonly { afterLesson: number; date: string; reweight: boolean }[] = [
  { afterLesson: 9, date: '2026-12-05', reweight: false },
  { afterLesson: 17, date: '2027-01-30', reweight: true },
  { afterLesson: 25, date: '2027-03-27', reweight: true },
  { afterLesson: 33, date: '2027-05-22', reweight: true },
  { afterLesson: 41, date: '2027-07-17', reweight: true },
];

/** The report shows only once the first block has ended (decision 11: after lesson 9, 8 before the Olla lesson). */
export const REPORT_FROM_LESSON = 9;

/** The latest block end reached by `day` (YYYY-MM-DD), or null before the first one. */
export function blockEndReached(day: string): (typeof BLOCK_ENDS)[number] | null {
  let out: (typeof BLOCK_ENDS)[number] | null = null;
  for (const b of BLOCK_ENDS) if (day >= b.date) out = b;
  return out;
}
